import { panelNavHtml } from '../panel-nav-html.js';

export interface SavingsPayload {
  domain: string;
  hoursPerWeek: number;
  hoursSavedPerWeek: number;
  annualHours: number;
  annualSavings: number;
  rate: number;
  automationPct: number;
}

const DOMAIN_LABEL: Record<string, string> = {
  finance: 'Finance ops',
  sales: 'Sales ops',
  support: 'Customer support',
  email: 'Email / lifecycle',
  social: 'Social / content',
  ops: 'Ops',
  generic: 'Manual work',
};

/**
 * Savings estimate card. Big animated counter up to the annual AUD
 * figure, with the underlying assumptions visible so it doesn't feel
 * like sales theatre.
 */
export class SavingsCard {
  private readonly root: HTMLElement;
  private raf?: number;

  constructor(root: HTMLElement) {
    this.root = root;
    this.root.classList.add('savings-overlay');
    this.root.addEventListener('click', (e) => {
      const t = e.target as HTMLElement;
      if (t === this.root || t.closest('[data-action="close"]')) {
        this.hide();
        document.dispatchEvent(new CustomEvent('merlin:nav-close', { bubbles: true }));
        return;
      }
      if (t.closest('[data-action="back"]')) {
        document.dispatchEvent(new CustomEvent('merlin:nav-back', { bubbles: true }));
      }
    });
  }

  show(payload: SavingsPayload) {
    const domain = DOMAIN_LABEL[payload.domain] ?? 'Manual work';
    this.root.innerHTML = `
      <div class="savings-sheet">
        ${panelNavHtml()}
        <div class="savings-badge">${escape(domain)} · Typical</div>
        <div class="savings-headline">
          <span class="savings-currency">$</span>
          <span class="savings-number" id="savings-number">0</span>
          <span class="savings-unit">/year</span>
        </div>
        <div class="savings-caveat">Estimated at ${payload.automationPct}% automation of ${payload.hoursPerWeek} hrs/week @ $${payload.rate}/hr</div>
        <div class="savings-breakdown">
          <div class="savings-cell">
            <div class="savings-cell-k">Hours saved</div>
            <div class="savings-cell-v">${payload.hoursSavedPerWeek}/week</div>
          </div>
          <div class="savings-cell">
            <div class="savings-cell-k">Annual hours</div>
            <div class="savings-cell-v">${payload.annualHours.toLocaleString()}</div>
          </div>
          <div class="savings-cell">
            <div class="savings-cell-k">Automation</div>
            <div class="savings-cell-v">${payload.automationPct}%</div>
          </div>
        </div>
        <div class="savings-note">Illustrative. Typical in our engagements — not a guarantee.</div>
      </div>
    `;
    this.root.classList.add('visible');
    this.countUp(payload.annualSavings);
  }

  hide() {
    this.root.classList.remove('visible');
    if (this.raf) cancelAnimationFrame(this.raf);
  }

  private countUp(target: number) {
    if (this.raf) cancelAnimationFrame(this.raf);
    const numEl = this.root.querySelector<HTMLElement>('#savings-number');
    if (!numEl) return;
    const duration = 1400;
    const start = performance.now();
    const step = (now: number) => {
      const t = Math.min(1, (now - start) / duration);
      const eased = 1 - Math.pow(1 - t, 3);
      const val = Math.round(target * eased);
      numEl.textContent = val.toLocaleString();
      if (t < 1) this.raf = requestAnimationFrame(step);
    };
    this.raf = requestAnimationFrame(step);
  }
}

function escape(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}
